import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { unsplashService, UnsplashPhoto } from '../services/unsplashService';
import { historyService } from '../services/historyService';

const SearchContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
`;

const SearchHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  font-size: 48px;
  font-weight: 900;
  color: var(--color-black);
  margin-bottom: 16px;
  letter-spacing: 2px;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: var(--color-gray);
  margin-bottom: 32px;
`;

const SearchForm = styled.form`
  display: flex;
  max-width: 600px;
  margin: 0 auto 40px;
  gap: 12px;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 16px 20px;
  font-size: 16px;
  border: 2px solid var(--color-black);
  background-color: var(--color-white);
  color: var(--color-black);
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    border-color: var(--color-blue);
  }

  &::placeholder {
    color: var(--color-gray);
  }
`;

const SearchButton = styled.button`
  padding: 16px 32px;
  font-size: 16px;
  font-weight: 600;
  background-color: var(--color-black);
  color: var(--color-white);
  border: 2px solid var(--color-black);
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: var(--color-white);
    color: var(--color-black);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid var(--color-white);
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ResultsContainer = styled.div`
  margin-top: 40px;
`;

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid var(--color-light-gray);
`;

const ResultsCount = styled.p`
  font-size: 16px;
  color: var(--color-gray);
  margin: 0;
`;

const LoadMoreButton = styled.button`
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  background-color: var(--color-black);
  color: var(--color-white);
  border: 2px solid var(--color-black);
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: var(--color-white);
    color: var(--color-black);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
`;

const PhotoCard = styled.div`
  position: relative;
  background-color: var(--color-white);
  border: 2px solid var(--color-light-gray);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: var(--color-black);
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
`;

const PhotoImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  display: block;
`;

const PhotoInfo = styled.div`
  padding: 16px;
`;

const PhotoTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: var(--color-black);
  margin: 0 0 8px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PhotoAuthor = styled.p`
  font-size: 14px;
  color: var(--color-gray);
  margin: 0 0 12px 0;
`;

const PhotoActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ $saved?: boolean }>`
  flex: 1;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  background-color: ${props => props.$saved ? '#10b981' : 'var(--color-black)'};
  color: var(--color-white);
  border: 2px solid ${props => props.$saved ? '#10b981' : 'var(--color-black)'};
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 0;

  &:hover {
    background-color: var(--color-white);
    color: ${props => props.$saved ? '#10b981' : 'var(--color-black)'};
    border-color: ${props => props.$saved ? '#10b981' : 'var(--color-black)'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: var(--color-gray);
  font-size: 16px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: var(--color-gray);
`;

const EmptyTitle = styled.h3`
  font-size: 24px;
  margin-bottom: 16px;
  color: var(--color-black);
`;

const EmptyDescription = styled.p`
  font-size: 16px;
  margin-bottom: 24px;
`;

const PopularSearches = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`;

const PopularTag = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  background-color: var(--color-white);
  color: var(--color-black);
  border: 2px solid var(--color-black);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--color-black);
    color: var(--color-white);
  }
`;

interface SearchScreenProps {
  isAuthenticated: boolean;
}

const SearchScreen: React.FC<SearchScreenProps> = ({ isAuthenticated }) => {
  const [query, setQuery] = useState('');
  const [photos, setPhotos] = useState<UnsplashPhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [savedPhotos, setSavedPhotos] = useState<Set<string>>(new Set());
  const [savingPhoto, setSavingPhoto] = useState<string | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);

  const popularSearches = [
    'nature', 'landscape', 'city', 'abstract', 'minimalist',
    'mountains', 'ocean', 'forest', 'sky', 'architecture'
  ];

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setError(null);
    setPhotos([]);
    setPage(1);
    setHasSearched(true);

    try {
      const response = await unsplashService.searchPhotos(query.trim());
      setPhotos(response.results);
      setTotalPages(response.total_pages);
      setTotalResults(response.total);
    } catch (err) {
      setError('Search failed, please try again later');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (loadingMore || page >= totalPages) return;

    setLoadingMore(true);
    try {
      const response = await unsplashService.searchPhotos(query, page + 1);
      setPhotos(prev => [...prev, ...response.results]);
      setPage(prev => prev + 1);
    } catch (err) {
      setError('Failed to load more, please try again later');
      console.error('Load more error:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handlePopularSearch = (tag: string) => {
    setQuery(tag);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleDownload = async (photo: UnsplashPhoto) => {
    try {
      const downloadUrl = await unsplashService.downloadPhoto(photo);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `wallpaper-${photo.id}.jpg`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Download error:', err);
      alert('Download failed, please try again later');
    }
  };

  const handleSave = async (photo: UnsplashPhoto) => {
    if (!isAuthenticated) {
      alert('Please login to save wallpapers');
      return;
    }

    if (savedPhotos.has(photo.id)) {
      return; // Already saved
    }

    setSavingPhoto(photo.id);

    try {
      const result = await historyService.saveOnlineWallpaper({
        title: photo.description || photo.alt_description || 'Untitled',
        imageUrl: photo.urls.full,
        thumbnailUrl: photo.urls.regular,
        author: `${photo.user.name} (@${photo.user.username})`,
        sourceId: photo.id
      });

      if (result.success) {
        setSavedPhotos(prev => new Set(Array.from(prev).concat(photo.id)));
        alert(`Saved! You have ${result.remainingSlots || 0} slots remaining.`);
      } else {
        alert(result.message || 'Failed to save wallpaper');
      }
    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to save wallpaper. Please try again.');
    } finally {
      setSavingPhoto(null);
    }
  };


  return (
    <SearchContainer>
      <SearchHeader>
        <Title>Search Wallpapers</Title>
        <Subtitle>Discover millions of high-quality wallpapers from Accord</Subtitle>
        
        <SearchForm onSubmit={handleSearch}>
          <SearchInput
            ref={searchInputRef}
            type="text"
            placeholder="Search wallpapers..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <SearchButton type="submit" disabled={loading}>
            {loading ? <LoadingSpinner /> : 'Search'}
          </SearchButton>
        </SearchForm>

        {!hasSearched && (
          <EmptyState>
            <EmptyDescription>Enter keywords to search for your favorite wallpapers</EmptyDescription>
            <PopularSearches>
              {popularSearches.map((tag) => (
                <PopularTag
                  key={tag}
                  onClick={() => handlePopularSearch(tag)}
                >
                  {tag}
                </PopularTag>
              ))}
            </PopularSearches>
          </EmptyState>
        )}
      </SearchHeader>

      {error && (
        <ErrorMessage>
          {error}
        </ErrorMessage>
      )}

      {photos.length > 0 && (
        <ResultsContainer>
          <ResultsHeader>
            <ResultsCount>
              Found {totalResults.toLocaleString()} images
            </ResultsCount>
          </ResultsHeader>

          <PhotoGrid>
            {photos.map((photo) => (
              <PhotoCard key={photo.id}>
                <PhotoImage
                  src={photo.urls.regular}
                  alt={photo.alt_description || photo.description || 'Unsplash photo'}
                  loading="lazy"
                />
                <PhotoInfo>
                  <PhotoTitle>
                    {photo.description || photo.alt_description || 'Untitled'}
                  </PhotoTitle>
                  <PhotoAuthor>
                    By {photo.user.name} (@{photo.user.username})
                  </PhotoAuthor>
                  <PhotoActions>
                    <ActionButton 
                      onClick={() => handleSave(photo)}
                      disabled={savingPhoto === photo.id || savedPhotos.has(photo.id)}
                      $saved={savedPhotos.has(photo.id)}
                    >
                      {savingPhoto === photo.id ? 'Saving...' : savedPhotos.has(photo.id) ? 'Saved' : 'Save'}
                    </ActionButton>
                    <ActionButton onClick={() => handleDownload(photo)}>
                      Download
                    </ActionButton>
                  </PhotoActions>
                </PhotoInfo>
              </PhotoCard>
            ))}
          </PhotoGrid>

          {page < totalPages && totalPages > 1 && (
            <div style={{ textAlign: 'center', marginTop: '40px' }}>
              <LoadMoreButton
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? <LoadingSpinner /> : 'Load More'}
              </LoadMoreButton>
            </div>
          )}
        </ResultsContainer>
      )}
    </SearchContainer>
  );
};

export default SearchScreen;
